import {
  FindLocationsByFilterDocument,
  FindLocationsByFilterQuery,
  FindLocationsByFilterQueryVariables,
  LayoutLocationFilter,
  ProximityPolicyConstraintType,
  ProximityPolicyFragment,
  ProximityPolicyInput,
  ProximityPolicyRuleFragment,
  ProximityPolicyRuleInput,
  ProximityPolicyRuleSubcategorySpecType,
  ProximityPolicyRuleType,
} from '@warebee/frontend/data-access-api-graphql';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { HTMLAttributes } from 'react';
import { secureClient } from '../GraphQLClient';
import { cloneWithoutTypename } from '../common/utils';
import * as Icon from '../components/icons';
import { getPolicyMatchInput } from '../policyFilters/policyFilter.helper';
import { ProximityPolicySelectedCategory } from './proximityPolicy.type';

export const proximityPolicyDefault: ProximityPolicyFragment = {
  rules: [],
};
export const proximityPolicyRuleDefault: ProximityPolicyRuleFragment = {
  mainCategory: {
    productsMatch: null,
    locationsMatch: null,
  },
  secondCategory: {
    productsMatch: null,
    locationsMatch: null,
  },
  type: ProximityPolicyRuleType.BETWEEN_CATEGORIES,
  subcategories: {
    breakBy: [
      {
        type: ProximityPolicyRuleSubcategorySpecType.SKU_GROUP,
      },
    ],
  },
  constraint: [
    {
      constraintType: ProximityPolicyConstraintType.NO_CLOSER_THAN,
      distance: {
        distance: 10,
        includeHeightFromFloor: false,
      },
    },
  ],
};

export function getProximityPolicyRuleInput(
  rule: ProximityPolicyRuleFragment,
): ProximityPolicyRuleInput {
  const clonedRule: ProximityPolicyRuleInput = cloneWithoutTypename(rule);
  const sourceItemsMatch = getPolicyMatchInput(
    rule?.mainCategory?.productsMatch,
  );
  const sourceLocsMatch = getPolicyMatchInput(
    rule?.mainCategory?.locationsMatch,
  );

  const destItemsMatch = getPolicyMatchInput(
    rule?.secondCategory?.productsMatch,
  );
  const destLocsMatch = getPolicyMatchInput(
    rule?.secondCategory?.locationsMatch,
  );

  const r: ProximityPolicyRuleInput = {
    ...clonedRule,
    mainCategory: {
      ...clonedRule.mainCategory,
      productsMatch: _.isEmpty(sourceItemsMatch?.anyOf)
        ? null
        : sourceItemsMatch,
      locationsMatch: _.isEmpty(sourceLocsMatch?.anyOf)
        ? null
        : sourceLocsMatch,
    },
    secondCategory: {
      ...clonedRule.secondCategory,
      productsMatch: _.isEmpty(destItemsMatch?.anyOf) ? null : destItemsMatch,
      locationsMatch: _.isEmpty(destLocsMatch?.anyOf) ? null : destLocsMatch,
    },
  };
  return r;
}

export function getProximityPolicyInput(
  policy: ProximityPolicyFragment,
): ProximityPolicyInput {
  return {
    rules: _.map(policy?.rules, getProximityPolicyRuleInput),
  };
}

export async function loadLocationsByRule(params: {
  layoutId: string;
  rule: ProximityPolicyRuleFragment;
  category: ProximityPolicySelectedCategory;
  filter: LayoutLocationFilter;
}): Promise<Set<string>> {
  const ruleInput = getProximityPolicyRuleInput(params.rule);
  const includeMatching =
    params.category === 'main'
      ? ruleInput.mainCategory?.locationsMatch
      : ruleInput.secondCategory?.locationsMatch;

  const response = await secureClient.query<
    FindLocationsByFilterQuery,
    FindLocationsByFilterQueryVariables
  >({
    query: FindLocationsByFilterDocument,
    variables: {
      page: {
        limit: null,
      },
      filter: params.filter,
      layoutId: params.layoutId,
      input: {
        includeMatching,
      },
    },
  });

  const ids = _.map(
    response.data?.layout?.locationsByFilter.content,
    l => l.locationId,
  );
  return new Set(ids);
}

export function getProximitySubcategoryOptions(t: TFunction<'simulation'>): {
  id: ProximityPolicyRuleSubcategorySpecType;
  title: string;
  icon?: React.FC<HTMLAttributes<Element>>;
}[] {
  return [
    // Item Related
    {
      id: ProximityPolicyRuleSubcategorySpecType.SKU,
      title: t(`Sku`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.SKU_GROUP,
      title: t(`Sku Group`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.STOCK_CATEGORY,
      title: t(`Stock Category`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.STORAGE_CLASS,
      title: t(`Storage Class`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.SUB_GROUP,
      title: t(`Sub Group`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.CONSIGNEE,
      title: t(`Consignee`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.NET_WEIGHT,
      title: t(`Net Weight`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.VOLUME,
      title: t(`Volume`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.POLLUTION_CLASS,
      title: t(`Pollution Class`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.TRANSPORT_CLASS,
      title: t(`Transport Class`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.USAGE_TYPE,
      title: t(`Usage Type`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },

    // Demand and Percent Ranks
    {
      id: ProximityPolicyRuleSubcategorySpecType.PERCENT_RANK,
      title: t(`Percent Rank`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.CUMULATIVE_PERCENT_RANK,
      title: t(`Cumulative Percent Rank`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.DAILY_ORDER_LINE_COUNT_AVG,
      title: t(`Daily Order Line Count Avg`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.DAILY_ORDER_LINE_COUNT_MAX,
      title: t(`Daily Order Line Count Max`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.TOTAL_ORDER_LINE_COUNT,
      title: t(`Total Order Line Count`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.TOTAL_QUANTITY_ORDERED,
      title: t(`Total Quantity Ordered`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.WEEKLY_DAYS_WITH_ORDERS_AVG,
      title: t(`Weekly Days With Orders Avg`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.WEEKLY_DAYS_WITH_ORDERS_MAX,
      title: t(`Weekly Days With Orders Max`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.WEEKLY_ORDER_LINE_COUNT_AVG,
      title: t(`Weekly Order Line Count Avg`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.WEEKLY_ORDER_LINE_COUNT_MAX,
      title: t(`Weekly Order Line Count Max`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },

    // Location Related
    {
      id: ProximityPolicyRuleSubcategorySpecType.CONGESTION_ZONE,
      title: t(`Congestion Zone`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.ACCESS_AISLE,
      title: t(`Access Aisle`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.AISLE,
      title: t(`Aisle`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.AREA,
      title: t(`Area`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.BAY,
      title: t(`Bay`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.BAY_LOCATION_ORDER,
      title: t(`Bay Location Order`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.BAY_TYPE,
      title: t(`Bay Type`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.ASSIGNMENT_COUNT,
      title: t(`Assignment Count`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.HEIGHT,
      title: t(`Height`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.HEIGHT_FROM_FLOOR,
      title: t(`Height From Floor`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.WIDTH,
      title: t(`Width`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.LENGTH,
      title: t(`Depth`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.DEPTH_FROM_FRONT,
      title: t(`Depth From Front`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.DEPTH_POSITION,
      title: t(`Depth Position`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.INDEX_FROM_FRONT,
      title: t(`Index From Front`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.LEVEL,
      title: t(`Level`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.LOCATION,
      title: t(`Location`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.MHTYPE,
      title: t(`MHType`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.PLANE,
      title: t(`Plane`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.PORTAL_X,
      title: t(`X`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.PORTAL_Y,
      title: t(`Y`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.RACKING_TYPE,
      title: t(`Location Type`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
    {
      id: ProximityPolicyRuleSubcategorySpecType.SIDE,
      title: t(`Side`, { ns: 'dataset' }),
      icon: Icon.LocationsOccupied,
    },
  ];
}

export function getProximityConstraintTypeOptions(t: TFunction<'simulation'>): {
  id: ProximityPolicyConstraintType;
  title: string;
  description?: string;
}[] {
  return [
    {
      id: ProximityPolicyConstraintType.NO_CLOSER_THAN,
      title: t(`No Closer Than`, { ns: 'dataset' }),
      description: t(`Items must be at least this distance apart`, {
        ns: 'dataset',
      }),
    },
  ];
}
